import { selectCurrentHotelId } from 'accessors'
import { updateBillableBookings } from 'actions/invoices'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import billingApi from 'services/api/billing'

import {
  blackListKeysConversionKeys,
  blacklistKeysToConversionKeys,
  getCleanConversion
} from '../../../main/blacklistHelpers'
import ConfirmationDialog from '../ConfirmationDialog/index'
import LoadingIndicator from '../LoadingIndicator'
import BlacklistContext from './BlackListContext'
import styles from './BlackListDialog.module.scss'
import BehaviorAndScope from './Steps/BehaviorAndScope/index'
import Confirmation from './Steps/Confirmation/index'
import Details from './Steps/Details/index'
import useRejectDetails from './useBlacklistDetails'

const NewRejectDialog = ({
  affectedBookings,
  onHide,
  onRejectBookings,
  onBlacklistBookings,
  isLoading
}) => {
  const dispatch = useDispatch()

  const currentHotelId = useSelector(selectCurrentHotelId)

  const { invoiceId } = useParams()

  const isMultiWithSameRate =
    affectedBookings.length > 1 &&
    affectedBookings.every(
      b => b.convertedRateCode === affectedBookings[0].convertedRateCode
    )

  const isMultiWithSameTracking =
    affectedBookings.length > 1 &&
    affectedBookings.every(
      b => b.convertedTrackingCode === affectedBookings[0].convertedTrackingCode
    )

  const isMultiBookings = affectedBookings.length > 1

  const rejectDetails = useRejectDetails()
  const conversion = getCleanConversion(affectedBookings[0])

  const hasPendingReconciliation = affectedBookings.some(
    b => b.lastReconciliationStatus === 'Pending'
  )

  const { exclusions, values, rateCodeCategory } = rejectDetails

  const [step, setStep] = useState(1)

  const titles = [
    'Which parts of data made you remove this booking?',
    'Select behavior and scope',
    `Do you want to exclude ${values.length > 1 ? 'these' : 'this'}?`,
    values.length > 1 ? 'Yes, exclude all' : 'Yes, exclude this'
  ]

  const cta = ['Continue', 'Continue', `Yes, exclude ${values.length > 1 ? 'all' : ''}`]

  async function handleConfirm() {
    if (step === 1) {
      setStep(2)
    }

    if (step === 2) {
      onRejectBookings(affectedBookings)

      const bookingsToReject = exclusions.filter(e => e.behavior !== 'noExclusion')

      if (rateCodeCategory) {
        await billingApi.categorizeRateCode({
          rateCode: conversion[blackListKeysConversionKeys.rateCodeFull],
          rateKind: rateCodeCategory
        })
        dispatch(updateBillableBookings(invoiceId, currentHotelId))
      }

      if (bookingsToReject.length > 0) {
        setStep(3)
      } else {
        onHide()
      }
    }

    if (step === 3) {
      const exclusionRules = exclusions
        .filter(e => e.behavior !== 'noExclusion')
        .map(b => ({
          ...b,
          itemValue: affectedBookings[0][blacklistKeysToConversionKeys[b.requestCategory]]
        }))

      onBlacklistBookings(exclusionRules)
      onHide()
    }
  }

  async function handleBack() {
    if (step > 1) {
      setStep(step - 1)
    } else {
      await onRejectBookings(affectedBookings)
      onHide()
    }
  }

  function getIsButtonDisabled() {
    const numberOfExceptions = Object.keys(values).length

    if (step === 1 && numberOfExceptions) {
      return false
    }

    if (step === 2 && exclusions.filter(o => !o.behavior || !o.scope).length === 0) {
      return false
    }

    if (step === 3) {
      return false
    }

    return true
  }

  return (
    <BlacklistContext.Provider value={{ ...rejectDetails, conversion }}>
      <ConfirmationDialog
        title={titles[step - 1]}
        theme="blacklist"
        onConfirm={handleConfirm}
        isButtonDisabled={getIsButtonDisabled()}
        positiveMessage={cta[step - 1]}
        hasDangerousAction={step === 3 && true}
        onReject={handleBack}
        negativeMessage={step > 1 ? 'Back' : 'None of them'}
        onClose={onHide}
        maxWidth={1300}
        contentPadding={20}
        bottomBarDetail={
          hasPendingReconciliation
            ? 'An updated billing email will be sent to customer'
            : null
        }
      >
        <div className={styles.container}>
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <>
              {step === 1 && (
                <Details
                  isMultiWithSameRate={isMultiWithSameRate}
                  isMultiWithSameTracking={isMultiWithSameTracking}
                  isMultiBookings={isMultiBookings}
                />
              )}
              {step === 2 && <BehaviorAndScope />}
              {step === 3 && <Confirmation />}
            </>
          )}
        </div>
      </ConfirmationDialog>
    </BlacklistContext.Provider>
  )
}

export default NewRejectDialog
